/**
 * Encargos: la proyección que un aliado externo SÍ puede ver.
 *
 * Diseño del plan del sistema (2026-08-17): las reglas de Firestore protegen
 * documentos completos, no campos. Por eso el aliado nunca lee `cotizaciones` —
 * vería precios y márgenes de todo—. Cuando una cotización incluye piezas de su
 * ramo, se proyecta este documento con SOLO su rebanada: pieza, cantidad,
 * habitación, fotos del levantamiento y notas. Ningún precio de venta viaja aquí.
 *
 * El levantamiento original queda sellado como la primera revisión del encargo;
 * cada cambio posterior es una revisión nueva (append-only), nunca una edición.
 */

export const RAMOS = {
  taller: {
    nombre: 'Taller — Madera y acero',
    /* Las 95 piezas del catálogo del taller comparten el prefijo TZ-; la partida que
       produce el levantamiento guiado de fabricación nace con prefijo MAD-
       (`levantamiento.js`, `tallerAProducto`). Sin el segundo, «Encargo al taller»
       nunca aparecía tras la guía — detectado en la refutación del 2026-08-22. */
    esDelRamo: sku => typeof sku === 'string' && (sku.startsWith('TZ-') || sku.startsWith('MAD-'))
  }
}

export const ESTADOS_ENCARGO = ['levantado', 'desarrollo', 'propuesta', 'aprobado', 'cotizado']

export const NOMBRE_ESTADO = {
  levantado: 'Levantado',
  desarrollo: 'En desarrollo',
  propuesta: 'Propuesta del taller',
  aprobado: 'Aprobado',
  cotizado: 'Cotizado al cliente'
}

/** Etiqueta humana de cada tipo de revisión. */
export const NOMBRE_TIPO = {
  levantamiento: 'Levantamiento',
  desarrollo: 'Avance del taller',
  nota: 'Nota de Soham'
}

/** Las piezas de un ramo dentro de una cotización, con lo que el aliado necesita
    para desarrollar — y nada más. `resolver(sku)` viene del contexto de la app. */
export function piezasDeRamo(cot, ramo, resolver) {
  const r = RAMOS[ramo]
  if (!r) return []
  const piezas = []
  for (const hab of cot.habitaciones || []) {
    for (const item of hab.items || []) {
      if (!r.esDelRamo(item.sku)) continue
      const p = (resolver && resolver(item.sku)) || item.def || {}
      piezas.push({
        sku: item.sku,
        nombre: p.nombre || item.sku,
        unidad: p.unidad || 'pieza',
        q: item.q || 1,
        habitacion: hab.nombre || '',
        // Las fotos del levantamiento sí viajan: son el insumo del desarrollo.
        fotos: (item.fotos || []).slice(0, 6),
        /* Y lo que el levantamiento guiado midió y decidió viaja también: el taller
           necesita el hueco en tres alturas, la escuadra, los módulos y las placas,
           no solo nombre y cantidad. Vacío cuando la pieza vino del catálogo. */
        resumen: Array.isArray(p.resumen) ? p.resumen : [],
        supuestos: Array.isArray(p.supuestos) ? p.supuestos : [],
        disenio: p.disenio || null
      })
    }
  }
  return piezas
}

/** Construye (o reconstruye) el documento de encargo. Devuelve null si la
    cotización no tiene piezas del ramo. Conserva id, fecha y estado previos. */
export function proyectaEncargo(cot, ramo, resolver, previo) {
  const piezas = piezasDeRamo(cot, ramo, resolver)
  if (!piezas.length) return null
  return {
    id: previo?.id || `${cot.id}--${ramo}`,
    ramo,
    cotizacionId: cot.id,
    /* El aliado ve el nombre del proyecto («Casa García»), nunca el contacto del
       cliente: el cliente es de Soham. */
    nombreProyecto: cot.nombre || 'Proyecto',
    fecha: previo?.fecha || cot.fecha || '',
    estado: previo?.estado || 'levantado',
    notas: cot.notasCot || '',
    piezas,
    actualizadoEn: new Date().toISOString()
  }
}
